import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiTruck,
  FiBriefcase,
  FiFilter,
  FiHash,
  FiUsers,
  FiFolder,
  FiUpload,
  FiDownload,
  FiLock,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Select,
  MultiSelect,
  Skeletons,
  Table,
  SupplierImportModal,
} from '../../components';
import TableActions from '../../components/Table/TableActions';
import EntityFilesModal from '../../components/EntityFilesModal';
import SupplierForm from '../../components/SupplierForm';
import '../CompanyManager/style.css';

export default function SupplierManager() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
  const [selectedSupplierForFiles, setSelectedSupplierForFiles] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  
  // Multi-company state
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [company, setCompany] = useState(null);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  const navigate = useNavigate();
  const permissions = usePermission('management');

  useEffect(() => {
    if (permissions.canView) {
      loadInitialData();
    }
  }, [permissions.canView]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilter]);

  async function loadInitialData() {
    try {
      setLoading(true);
      const companiesData = await Api.getCompanies();
      if (Array.isArray(companiesData)) {
        setCompanies(companiesData);
        // Default to 'all' per user preference
        setCompanyFilter('all');
        await loadSuppliers('all', companiesData);
      }
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
      setError('Erro ao carregar dados iniciais.');
    } finally {
      setLoading(false);
    }
  }

  async function loadSuppliers(filterValue = companyFilter, companiesList = companies) {
    try {
      setLoading(true);
      let data = [];

      if (filterValue === 'all') {
        // Fetch for all companies
        const promises = companiesList.map(comp => 
          Api.getSuppliers(comp.id).then(res => 
            Array.isArray(res) ? res.map(s => ({ ...s, company: comp, company_id: comp.id })) : []
          ).catch(() => [])
        );
        const results = await Promise.all(promises);
        data = results.flat();
      } else {
        // Fetch for specific company
        const res = await Api.getSuppliers(filterValue);
        if (Array.isArray(res)) {
          const selectedComp = companiesList.find(c => String(c.id) === String(filterValue));
          data = res.map(s => ({ ...s, company: selectedComp, company_id: filterValue }));
          setCompany(selectedComp);
        }
      }

      if (Array.isArray(data)) {
        setSuppliers(data);
      } else {
        setError('Não foi possível carregar os fornecedores.');
      }
    } catch (err) {
      setError('Erro ao carregar fornecedores. Tente novamente.');
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }

  function handleCreate() {
    setSelectedSupplier(null);
    setIsModalOpen(true);
  }

  function handleEdit(supplier) {
    setSelectedSupplier(supplier);
    setIsModalOpen(true);
  }

  function handleFiles(supplier) {
    setSelectedSupplierForFiles(supplier);
    setIsFilesModalOpen(true);
  }

  function handleFormSuccess() {
    loadSuppliers(companyFilter);
    setIsModalOpen(false);
  }

  function handleExport() {
    const exportData = suppliers.map(s => ({
      'Empresa': s.company?.trade_name || s.company?.name || 'N/A',
      'Nome / Razão Social': s.name,
      'Nome Fantasia': s.trade_name,
      'Tipo': s.type,
      'CPF / CNPJ': s.document,
      'E-mail': s.email,
      'Telefone': s.phone,
      'Celular': s.mobile,
      'CEP': s.zip_code,
      'Endereço': s.street,
      'Número': s.number,
      'Complemento': s.complement,
      'Bairro': s.neighborhood,
      'Cidade': s.city,
      'Estado': s.state,
      'Observações': s.notes,
      'Ativo': s.active ? 'Sim' : 'Não'
    }));
  
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fornecedores");
    XLSX.writeFile(wb, "fornecedores.xlsx");
  }

  function handleDeleteClick(supplier) {
    setSupplierToDelete(supplier);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!supplierToDelete) return;

    // Use supplier's company_id or fallback to current context
    const targetCompanyId = supplierToDelete.company_id || supplierToDelete.company?.id || company?.id;
    if (!targetCompanyId) {
        alert('Erro: Empresa não identificada para exclusão.');
        return;
    }

    try {
      setIsDeleting(true);
      await Api.deleteSupplier(targetCompanyId, supplierToDelete.id);
      loadSuppliers(companyFilter);
      setIsDeleteModalOpen(false);
      setSupplierToDelete(null);
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir fornecedor.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      const id = idsWithoutAll[0];
      setCompanyFilter(id);
      loadSuppliers(id);
    } else {
      setCompanyFilter('all');
      loadSuppliers('all');
    }
  }

  const filteredSuppliers = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return suppliers.filter(supplier => {
      const matchesSearch =
        supplier.name?.toLowerCase().includes(searchLower) ||
        supplier.trade_name?.toLowerCase().includes(searchLower) ||
        supplier.document?.toLowerCase()?.includes(searchLower) ||
        supplier.email?.toLowerCase().includes(searchLower);

      const companyId = supplier.company?.id || supplier.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesCompany;
    });
  }, [suppliers, searchTerm, companyFilterValues]);

  const totalItems = filteredSuppliers.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedSuppliers = filteredSuppliers.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'supplier',
      header: 'Fornecedor',
      width: '2.4fr',
      cellClassName: 'manager-table-cell-primary',
      render: supplier => (
        <>
          <div className="manager-table-avatar">
            {supplier.type === 'PJ' ? (
              <FiBriefcase size={18} />
            ) : (
              <FiUsers size={18} />
            )}
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {supplier.name}
            </span>
            <span className="manager-table-subtitle">
              {supplier.trade_name || `ID: ${supplier.id}`}
            </span>
          </div>
        </>
      ),
    },
    {
      id: 'document',
      header: 'Documento',
      width: '1.8fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: supplier => (
        <div className="manager-table-cnpj">
          <FiHash size={14} />
          <span>{supplier.document || 'Não informado'}</span>
        </div>
      ),
    },
    {
      id: 'contact',
      header: 'Contato',
      width: '2fr',
      cellClassName: 'manager-table-cell',
      render: supplier => {
        const email = supplier.email || 'E-mail não informado';
        const phone = supplier.mobile || supplier.phone || 'Telefone não informado';

        return (
          <div className="manager-table-cell-text">
            <span className="manager-table-subtitle">
              {email}
            </span>
            <span className="manager-table-subtitle">
              {phone}
            </span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-tags',
      render: supplier => {
        const isActive = supplier.active !== false;

        return (
          <div className="manager-tags">
            <span
              className={[
                'manager-tag',
                isActive ? 'manager-tag-success' : 'manager-tag-danger',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: row => {
        const companyName = row.company ? (row.company.trade_name || row.company.name) : 'N/A';
        return <span className="manager-table-text" style={{ fontWeight: 'normal' }}>{companyName}</span>;
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: supplier => (
        <TableActions>
          {permissions.canEdit && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(supplier);
                }}
              >
                <FiEdit2 size={16} />
                Editar
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFiles(supplier);
                }}
              >
                <FiFolder size={16} />
                Arquivos
              </button>
            </>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(supplier);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function SupplierManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  const companyOptions = useMemo(() => {
    const opts = companies.map(c => ({
      value: c.id,
      label: c.trade_name || c.name
    }));
    return [{ value: 'all', label: 'TODAS' }, ...opts];
  }, [companies]);

  const isMobile = window.innerWidth <= 768;

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <SupplierManagerSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiTruck size={22} />
                </div>
                <div>
                  <h1>Fornecedores</h1>
                  <p>
                    Visualize, filtre e gerencie todos os fornecedores da empresa selecionada.
                  </p>
                </div>
              </div>
              <div className="manager-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {permissions.canCreate && (
                  <>
                    <Button onClick={() => setIsImportModalOpen(true)} className="manager-header-button" variant="secondary">
                        <FiUpload size={18} />
                        {!isMobile && 'Importar'}
                    </Button>
                    <Button onClick={handleExport} className="manager-header-button" variant="secondary">
                        <FiDownload size={18} />
                        {!isMobile && 'Exportar'}
                    </Button>
                    <Button onClick={handleCreate} className="manager-header-button">
                        <FiPlus size={18} />
                        {'Fornecedor'}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {suppliers.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredSuppliers.length} de {suppliers.length} fornecedores
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="company-filter"
                    options={companyOptions}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="supplier-search"
                      placeholder="Busque por nome, documento ou e-mail"
                      value={searchTerm}
                      onChange={event =>
                        setSearchTerm(event.target.value)
                      }
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!error && suppliers.length === 0 && (
              <div className="empty-state">
                <p>Nenhum fornecedor cadastrado.</p>
              </div>
            )}

            {!error && suppliers.length > 0 && (
              <Table
                items={paginatedSuppliers}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={supplier => supplier.id}
                emptyMessage="Nenhum fornecedor encontrado."
              />
            )}
          </>
        )}
      </div>

      <EntityFilesModal
        isOpen={isFilesModalOpen}
        onClose={() => setIsFilesModalOpen(false)}
        entity={selectedSupplierForFiles}
        entityType="supplier"
        company={selectedSupplierForFiles?.company || company}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        size='xl'
      >
        <SupplierForm
          companyId={selectedSupplier?.company_id || (companyFilter === 'all' ? null : companyFilter)}
          companies={companies}
          initialData={selectedSupplier}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar Exclusão"
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o fornecedor{' '}
            <span className="delete-modal-company-name">
              {supplierToDelete?.name}
            </span>
            ?
          </p>
          <p>Essa ação não poderá ser desfeita.</p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>

      {/* Add SupplierImportModal if it was supposed to be here, but I'll skip it since it wasn't rendered before */}
      <SupplierImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => loadSuppliers(companyFilter)}
        companyId={companyFilter !== 'all' ? companyFilter : null}
        companies={companies}
      />
    </PageComponent>
  );
}
